// dirtyTracking.js
//
// Pure, side-effect-free helpers that back the Project_Editor's unsaved-changes
// ("dirty") tracking. They compare the editor's current form state against the
// last saved baseline and report whether any tracked field differs. Every
// helper is total (never throws, never mutates its input), mirroring the
// conventions used by `projectListDerivation.js` and `projectNormalizer.js`.
//
// These helpers are consumed by ProjectEditor.vue (tasks 13.1, 13.3, 13.4) and
// are covered by a property test (task 13.2):
//   - Property 19: Unsaved-changes dirty tracking (Requirements 11.1, 11.4)
//
// The editor reports unsaved changes if and only if some tracked field differs
// from the saved baseline. Establishing a fresh baseline (initial load, reset,
// or a successful save) makes the state report no unsaved changes, so
// subsequent navigation does not trigger the unsaved-changes prompt
// (Requirement 11.4).
//
// FormState (the tracked subset of the Project_Editor fields):
//   {
//     title,            // string
//     description,      // string
//     project_type_id,  // Service_Category reference (number|string)
//     client_id,        // Client_Record reference (number|string)
//     show_on_website,  // boolean
//     image_reference,  // http/https URL or upload key (string)
//     image_alt         // string
//   }

/**
 * The Project_Editor fields tracked for unsaved-changes detection. A change to
 * any of these fields relative to the baseline marks the editor dirty.
 * @type {ReadonlyArray<string>}
 */
export const TRACKED_FIELDS = Object.freeze([
  'title',
  'description',
  'project_type_ids',
  'client_id',
  'show_on_website',
  'image_reference',
  'image_alt'
])

/**
 * Canonicalize a single field value into a comparable primitive so that
 * equivalent-but-differently-typed values (e.g. `null` vs `undefined` vs `''`,
 * or a numeric id vs its string form) compare equal and do not produce a
 * spurious "dirty" result.
 *
 * - null / undefined            -> ''   (all "absent" values are equivalent)
 * - string                      -> the string verbatim
 * - finite number               -> its string form (so 7 === '7')
 * - boolean                     -> the boolean verbatim
 * - anything else (objects etc) -> a stable JSON-ish string, or '' if not
 *                                  serializable
 *
 * @param {*} value
 * @returns {(string|boolean)}
 */
function canonicalizeValue (value) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : ''
  if (typeof value === 'boolean') return value
  // Arrays (e.g. the Service_Category multi-select `project_type_ids`) compare
  // by their sorted contents so selection order never registers as a change.
  if (Array.isArray(value)) {
    try {
      return JSON.stringify(value.slice().sort())
    } catch (e) {
      return ''
    }
  }
  try {
    return JSON.stringify(value)
  } catch (e) {
    return ''
  }
}

/**
 * Normalize an arbitrary form-state object into a canonical, comparable object
 * containing exactly the {@link TRACKED_FIELDS}. Tolerates a null/non-object
 * input by treating every field as absent. Pure and total: never mutates the
 * input and never throws.
 *
 * @param {*} state - a raw Project_Editor form state
 * @returns {Object} a canonical object keyed by every tracked field
 */
export function normalizeFormState (state) {
  const source = state && typeof state === 'object' && !Array.isArray(state) ? state : {}
  const normalized = {}
  for (const field of TRACKED_FIELDS) {
    normalized[field] = canonicalizeValue(source[field])
  }
  return normalized
}

/**
 * Return the list of tracked fields whose current value differs from the saved
 * baseline. Field values are compared after canonicalization, so absent values
 * and equivalent numeric/string ids do not register as changes. Pure and
 * total: never mutates its inputs and never throws.
 *
 * @param {*} baseline - the last saved form state
 * @param {*} current - the current form state
 * @returns {string[]} the names of the changed fields (possibly empty)
 */
export function changedFields (baseline, current) {
  const base = normalizeFormState(baseline)
  const now = normalizeFormState(current)
  return TRACKED_FIELDS.filter(field => base[field] !== now[field])
}

/**
 * Report whether the current form state has unsaved changes relative to the
 * saved baseline (Requirements 11.1, 11.4).
 *
 * Returns true if and only if some tracked field differs from the baseline.
 * When `current` equals the baseline (including immediately after establishing
 * a fresh baseline on load, reset, or successful save) this returns false, so
 * navigation does not trigger the unsaved-changes prompt. Pure and total.
 *
 * @param {*} baseline - the last saved form state
 * @param {*} current - the current form state
 * @returns {boolean} true when there are unsaved changes
 */
export function isDirty (baseline, current) {
  return changedFields(baseline, current).length > 0
}

/**
 * Produce a fresh baseline snapshot from a form state, to be captured on
 * initial load, on reset, and after a successful save (Requirement 11.4).
 * The returned snapshot is a normalized, self-contained object that is safe to
 * retain and compare against later form states without holding a reference to
 * the live form.
 *
 * @param {*} state - the form state to snapshot as the new baseline
 * @returns {Object} the baseline snapshot
 */
export function captureBaseline (state) {
  return normalizeFormState(state)
}

export default {
  TRACKED_FIELDS,
  normalizeFormState,
  changedFields,
  isDirty,
  captureBaseline
}
